import { axiosAuthInstance } from '../../../../configs/AxiosInstance';
import { filenameFromDisposition } from '../constants/downloadNames';

/**
 * Transport shared by `linksApi.js` and `leadFormsApi.js`.
 *
 * - Everything goes through `axiosAuthInstance` (`…/auth/welldhan`) with the
 *   bearer token.
 * - JSON calls resolve to the envelope's `data`; blob calls resolve to
 *   `{ blob, filename }` (filename null when `Content-Disposition` is unreadable).
 * - Errors are thrown as-is (axios errors).
 */

export const authHeaders = (extra = {}) => {
  const token = localStorage.getItem('access_token');
  return { ...(token ? { Authorization: `Bearer ${token}` } : {}), ...extra };
};

/**
 * Drop empty values and turn arrays into the csv the API expects.
 * @param {Record<string, any>} params
 * @returns {Record<string, string|number|boolean>}
 */
export function toQuery(params = {}) {
  return Object.fromEntries(
    Object.entries(params)
      .filter(([, value]) => value !== undefined && value !== null && value !== '')
      .map(([key, value]) => [key, Array.isArray(value) ? value.join(',') : value])
      .filter(([, value]) => value !== '')
  );
}

export const unwrap = (response) => {
  const body = response?.data;
  return body && typeof body === 'object' && 'data' in body && 'success' in body ? body.data : body;
};

export const enc = (value) => encodeURIComponent(String(value ?? ''));
export const encCode = (code) => enc(String(code ?? '').toUpperCase());

export async function getJson(path, params, { signal } = {}) {
  const response = await axiosAuthInstance.get(path, {
    params: toQuery(params),
    signal,
    headers: authHeaders(),
  });
  return unwrap(response);
}

export async function postJson(path, body, { signal, headers, timeout } = {}) {
  const response = await axiosAuthInstance.post(path, body, {
    signal,
    timeout,
    headers: authHeaders({ 'Content-Type': 'application/json', ...headers }),
  });
  return unwrap(response);
}

export const readDisposition = (headers) => {
  if (!headers) return null;
  const value =
    typeof headers.get === 'function' ? headers.get('content-disposition') : headers['content-disposition'];
  return filenameFromDisposition(value);
};

/**
 * Read a Blob as text. jsdom and older browsers lack `Blob.prototype.text`,
 * so fall back to FileReader.
 * @param {Blob} blob
 * @returns {Promise<string>}
 */
function blobText(blob) {
  if (typeof blob.text === 'function') return blob.text();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result ?? ''));
    reader.onerror = () => reject(reader.error);
    reader.readAsText(blob);
  });
}

/**
 * With `responseType: 'blob'`, axios hands error bodies back as a Blob, so
 * `readLinkError` cannot see `detail.code`. Decode a JSON body and rethrow a
 * copy of the error carrying the parsed object. Anything else (abort, network
 * error, non-JSON body) is rethrown as-is.
 * @param {any} error
 * @returns {Promise<never>}
 */
export async function rethrowBlobError(error) {
  const response = error?.response;
  const isBlobBody = typeof Blob !== 'undefined' && response?.data instanceof Blob;
  if (!isBlobBody) throw error;
  let parsed;
  try {
    parsed = JSON.parse(await blobText(response.data));
  } catch {
    throw error;
  }
  const copy = Object.assign(Object.create(Object.getPrototypeOf(error)), error, {
    response: { ...response, data: parsed },
  });
  throw copy;
}

/**
 * @param {string} path
 * @param {Record<string, any>} [params]
 * @param {{ signal?: AbortSignal, onDownloadProgress?: (event: import('axios').AxiosProgressEvent) => void }} [opts]
 *   `onDownloadProgress` is handed to axios as-is (exports show a progress bar).
 */
export async function getBlob(path, params, { signal, onDownloadProgress } = {}) {
  const response = await axiosAuthInstance.get(path, {
    params: toQuery(params),
    signal,
    ...(onDownloadProgress ? { onDownloadProgress } : {}),
    responseType: 'blob',
    headers: authHeaders(),
  }).catch(rethrowBlobError);
  return { blob: response.data, filename: readDisposition(response.headers) };
}
